import * as XLSXStyle from "xlsx-js-style";
import {
  texto, identificador, numero, moneda, centro, totalizar, construirHojaTabla, descargarLibro, limpiarNombreArchivo,
} from "../../../../Components/ExcelTablaReporte";
import { calcularImporte, redondear2, fmtMXN } from "./aguinaldoUtils";

/**
 * Excel para EDITAR el borrador de aguinaldo: se descarga la tabla del paso Empleados, se corrige
 * en Excel y se vuelve a cargar. No es el Excel de impresión (aguinaldoExcel.js): éste trae a todos
 * los empleados del borrador, incluidos o no, con la columna Id para cruzarlos de vuelta.
 *
 * Columnas editables (en amarillo): Incluir, Incidencias (sólo si se descuentan), Días trabajados e
 * Importe. Días trabajados e Importe van como FÓRMULA cuando en pantalla salen de la fórmula, para
 * que Excel los recalcule mientras se edita; si en pantalla están ajustados a mano, van como valor.
 *
 * Al cargar se aplican las MISMAS reglas que la tabla (AsistenteAguinaldo.onCambiarFila):
 *   · incidencias cambiadas → días trabajados = días del ejercicio − incidencias;
 *   · días cambiados → el importe regresa a la fórmula;
 *   · importe distinto de la fórmula → importe manual.
 *
 * Sin React ni antd, para poder probarlo en node.
 */

// Una sola lista de columnas para escribir y para leer: el lector busca por TÍTULO, no por
// posición, así que agregar o quitar columnas no rompe archivos ya descargados.
const COLUMNAS = [
  { clave: "codigo", titulo: "No.", tipo: "identificador", ancho: 8 },
  { clave: "nombre", titulo: "Empleado", tipo: "texto", ancho: 36 },
  { clave: "prenomina", titulo: "Prenómina", tipo: "texto", ancho: 28, soloOrdinaria: true },
  { clave: "incluido", titulo: "Incluir (Sí/No)", tipo: "centro", ancho: 10, editable: true },
  { clave: "antiguedadAnios", titulo: "Antigüedad (años)", tipo: "numero", ancho: 11 },
  { clave: "diasAguinaldo", titulo: "Días de aguinaldo", tipo: "numero", ancho: 11 },
  { clave: "diasCalendario", titulo: "Días del ejercicio", tipo: "numero", ancho: 11 },
  { clave: "diasIncidencias", titulo: "Incidencias", tipo: "numero", ancho: 11, editable: true, soloConIncidencias: true },
  { clave: "diasTrabajados", titulo: "Días trabajados", tipo: "numero", ancho: 11, editable: true },
  { clave: "salarioDiario", titulo: "Salario diario", tipo: "moneda", ancho: 14 },
  { clave: "importe", titulo: "Importe", tipo: "moneda", ancho: 15, editable: true },
  { clave: "avisos", titulo: "Avisos", tipo: "texto", ancho: 44 },
  { clave: "id", titulo: "Id", tipo: "identificador", ancho: 26 },
];

const DIAS_MAXIMOS = 366;
// Excel redondea con ROUND y la pantalla con Math.round: un centavo de diferencia sigue siendo la
// fórmula, no un importe capturado a mano.
const TOLERANCIA_IMPORTE = 0.0101;

const RELLENO_EDITABLE = { fgColor: { rgb: "FFF4CC" } };
const RELLENO_ENCABEZADO_EDITABLE = { fgColor: { rgb: "D48806" } };

const norm = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const esVacio = (v) => String(v ?? "").trim() === "";

const codigoTexto = (c) => (c === null || c === undefined || c === "" ? "" : String(c).padStart(4, "0"));

const siNo = (b) => (b ? "Sí" : "No");

const columnasPara = ({ tipoAplicacion, descontarIncidencias }) =>
  COLUMNAS.filter((c) => (!c.soloOrdinaria || tipoAplicacion === "ordinaria")
    && (!c.soloConIncidencias || descontarIncidencias));

const avisosFila = (e) => [
  e.noAplicable,
  e.yaTieneAguinaldo ? `Ya tiene aguinaldo: ${e.yaTieneAguinaldo}` : null,
  e.sinTablaPrestacion ? "Sin tabla de prestaciones: 15 días de ley" : null,
  e.incidenciasManual ? "Incidencias corregidas a mano" : null,
  e.importeManual ? "Importe manual" : null,
].filter(Boolean).join(" · ");

// Días trabajados ajustados a mano: ya no son "días del ejercicio − incidencias".
const diasAjustados = (e) =>
  (Number(e.diasTrabajados) || 0) !== Math.max(0, (Number(e.diasCalendario) || 0) - (Number(e.diasIncidencias) || 0));

// ─── Escribir ────────────────────────────────────────────────────────────────

/**
 * La hoja "Aguinaldo" lista para descargar.
 * @param {object} p
 * @param {Array}  p.empleados             filas del borrador (state.empleados del asistente)
 * @param {number} p.diasAnio
 * @param {number} p.ejercicio
 * @param {string} p.tipoAplicacion        "ordinaria" | "extraordinaria"
 * @param {boolean} p.descontarIncidencias
 * @param {(id: string) => string} [p.nombrePrenomina]
 * @param {string} [p.empresaNombre]
 */
export function construirHojaEdicion({
  empleados = [], diasAnio = 365, ejercicio, tipoAplicacion, descontarIncidencias, nombrePrenomina, empresaNombre,
}) {
  const columnas = columnasPara({ tipoAplicacion, descontarIncidencias });
  const valor = {
    codigo: (e) => identificador(codigoTexto(e.codigo)),
    nombre: (e) => texto(e.nombre),
    prenomina: (e) => texto(e.prenominaId && nombrePrenomina ? nombrePrenomina(e.prenominaId) : ""),
    incluido: (e) => centro(siNo(e.incluido && !e.noAplicable)),
    antiguedadAnios: (e) => numero(Number(e.antiguedadAnios) || 0),
    diasAguinaldo: (e) => numero(Number(e.diasAguinaldo) || 0),
    diasCalendario: (e) => numero(Number(e.diasCalendario) || 0),
    diasIncidencias: (e) => numero(Number(e.diasIncidencias) || 0),
    diasTrabajados: (e) => numero(Number(e.diasTrabajados) || 0),
    salarioDiario: (e) => moneda(Number(e.salarioDiario) || 0),
    importe: (e) => moneda(redondear2(e.importe)),
    avisos: (e) => texto(avisosFila(e)),
    id: (e) => identificador(String(e.empleadoId)),
  };
  const filas = empleados.map((e) => columnas.map((c) => valor[c.clave](e)));

  // Sólo el importe se suma: un total de salarios diarios no significa nada.
  const totales = totalizar(columnas, filas);
  totales[columnas.findIndex((c) => c.clave === "salarioDiario")] = null;

  const hoja = construirHojaTabla(
    { columnas, filas, totales },
    {
      titulo: `Aguinaldo ${ejercicio} (borrador) · Edición`,
      empresaNombre,
      descripcionFiltros: "Edita sólo las columnas en amarillo: Incluir (Sí/No)"
        + `${descontarIncidencias ? ", Incidencias" : ""}, Días trabajados e Importe. `
        + "No cambies la columna Id: con ella se cruza cada renglón al cargar el archivo.",
    }
  );

  // ── Parche: relleno de las editables y fórmulas ──
  const filaEncabezado = buscarFilaEncabezado(hoja, columnas[0].titulo);
  const col = (clave) => {
    const i = columnas.findIndex((c) => c.clave === clave);
    return i < 0 ? null : XLSXStyle.utils.encode_col(i);
  };
  const letra = {
    diasAguinaldo: col("diasAguinaldo"),
    diasCalendario: col("diasCalendario"),
    diasIncidencias: col("diasIncidencias"),
    diasTrabajados: col("diasTrabajados"),
    salarioDiario: col("salarioDiario"),
    importe: col("importe"),
  };

  columnas.forEach((c, i) => {
    if (!c.editable) return;
    const encabezado = hoja[XLSXStyle.utils.encode_cell({ r: filaEncabezado, c: i })];
    if (encabezado) encabezado.s = { ...encabezado.s, fill: RELLENO_ENCABEZADO_EDITABLE };
    empleados.forEach((_, k) => {
      const celda = hoja[XLSXStyle.utils.encode_cell({ r: filaEncabezado + 1 + k, c: i })];
      if (celda) celda.s = { ...celda.s, fill: RELLENO_EDITABLE };
    });
  });

  empleados.forEach((e, k) => {
    const r = filaEncabezado + 2 + k; // renglón de Excel (base 1)
    if (descontarIncidencias && !diasAjustados(e)) {
      const celda = hoja[`${letra.diasTrabajados}${r}`];
      if (celda) celda.f = `MAX(0,${letra.diasCalendario}${r}-${letra.diasIncidencias}${r})`;
    }
    if (!e.importeManual) {
      const celda = hoja[`${letra.importe}${r}`];
      if (celda) {
        celda.f = `ROUND(${letra.diasAguinaldo}${r}/${Number(diasAnio) || 365}*${letra.diasTrabajados}${r}*${letra.salarioDiario}${r},2)`;
      }
    }
  });

  if (empleados.length) {
    const primera = filaEncabezado + 2;
    const ultima = filaEncabezado + 1 + empleados.length;
    const total = hoja[`${letra.importe}${ultima + 1}`];
    if (total) total.f = `SUM(${letra.importe}${primera}:${letra.importe}${ultima})`;
  }

  return hoja;
}

// Fila (base 0) de los encabezados de la tabla dentro de una hoja de construirHojaTabla.
function buscarFilaEncabezado(hoja, primerTitulo) {
  for (let r = 0; r < 20; r++) {
    if (hoja[XLSXStyle.utils.encode_cell({ r, c: 0 })]?.v === primerTitulo) return r;
  }
  throw new Error("No se encontró el encabezado de la tabla");
}

export const nombreArchivoEdicion = (ejercicio) => limpiarNombreArchivo(`Aguinaldo_${ejercicio}_editar`);

export const descargarExcelEdicion = (params) => {
  descargarLibro([{ nombre: "Aguinaldo", hoja: construirHojaEdicion(params) }], nombreArchivoEdicion(params.ejercicio));
};

// ─── Leer ────────────────────────────────────────────────────────────────────

// undefined = celda vacía (sin cambio); NaN = no es número.
const leerNumero = (v) => {
  if (esVacio(v)) return undefined;
  if (typeof v === "number") return v;
  const limpio = String(v).replace(/[$\s,]/g, "");
  return limpio === "" ? undefined : Number(limpio);
};

// undefined = vacío (sin cambio); null = no se entendió.
const leerSiNo = (v) => {
  if (esVacio(v)) return undefined;
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  const t = norm(v);
  if (["si", "s", "x", "1", "true", "verdadero"].includes(t)) return true;
  if (["no", "n", "0", "false", "falso"].includes(t)) return false;
  return null;
};

const fmtNum = (n) => (Number(n) || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 });

/**
 * Cruza el archivo contra el borrador y calcula, renglón por renglón, cómo quedaría cada empleado.
 *
 * @param {object} p
 * @param {Array<Array>} p.filas   sheet_to_json(hoja, { header: 1, raw: true, defval: null })
 * @param {Array}  p.empleados     filas del borrador
 * @param {number} p.diasAnio
 * @param {boolean} p.descontarIncidencias
 * @returns {{ error?: string, renglones?: Array, resumen?: object, avisos?: string[] }}
 *   renglón: { filaExcel, empleadoId, nombre, estado: "cambia"|"igual"|"error", cambios, errores, avisos, nuevo }
 */
export function analizarLibroEdicion({ filas = [], empleados = [], diasAnio = 365, descontarIncidencias }) {
  const iEncabezado = filas.findIndex((f) => (f || []).some((c) => norm(c) === "id")
    && (f || []).some((c) => norm(c) === "empleado"));
  if (iEncabezado < 0) {
    return { error: 'No se encontró el renglón de encabezados (columnas "Empleado" e "Id"). Descarga el Excel de esta pantalla y edita sobre él.' };
  }
  const encabezado = filas[iEncabezado].map(norm);
  const idx = {};
  COLUMNAS.forEach((c) => { idx[c.clave] = encabezado.indexOf(norm(c.titulo)); });

  const avisos = [];
  if (idx.diasIncidencias >= 0 && !descontarIncidencias) {
    avisos.push("El borrador no descuenta incidencias: la columna Incidencias del archivo se ignora.");
  }
  const usaIncidencias = descontarIncidencias && idx.diasIncidencias >= 0;

  const porId = new Map(empleados.map((e) => [String(e.empleadoId), e]));
  // Respaldo por número de empleado, sin ceros a la izquierda. Un número repetido no sirve de llave.
  const porCodigo = new Map();
  const codigosRepetidos = new Set();
  for (const e of empleados) {
    const k = String(e.codigo ?? "").replace(/^0+/, "");
    if (!k) continue;
    if (porCodigo.has(k)) codigosRepetidos.add(k);
    else porCodigo.set(k, e);
  }

  const vistos = new Set();
  const renglones = [];
  for (let i = iEncabezado + 1; i < filas.length; i++) {
    const fila = filas[i] || [];
    if (fila.every(esVacio)) continue;
    if (norm(fila[0]) === "total") continue;

    const filaExcel = i + 1;
    const idTxt = idx.id >= 0 ? String(fila[idx.id] ?? "").trim() : "";
    const codigoTxt = idx.codigo >= 0 ? String(fila[idx.codigo] ?? "").trim().replace(/^0+/, "") : "";
    const nombreArchivo = idx.nombre >= 0 ? String(fila[idx.nombre] ?? "").trim() : "";

    let e = idTxt ? porId.get(idTxt) : null;
    if (!e && !idTxt && codigoTxt && !codigosRepetidos.has(codigoTxt)) e = porCodigo.get(codigoTxt);

    const renglon = {
      filaExcel,
      empleadoId: e ? String(e.empleadoId) : null,
      nombre: e ? e.nombre : nombreArchivo,
      estado: "igual",
      cambios: [],
      errores: [],
      avisos: [],
      nuevo: null,
    };
    renglones.push(renglon);

    if (!e) {
      renglon.estado = "error";
      renglon.errores.push(idTxt || codigoTxt ? "No está en este borrador" : "Sin Id ni número de empleado");
      continue;
    }
    if (vistos.has(renglon.empleadoId)) {
      renglon.estado = "error";
      renglon.errores.push("Repetido en el archivo: sólo se toma su primer renglón");
      continue;
    }
    vistos.add(renglon.empleadoId);

    const incluir = idx.incluido >= 0 ? leerSiNo(fila[idx.incluido]) : undefined;
    const inc = usaIncidencias ? leerNumero(fila[idx.diasIncidencias]) : undefined;
    const trab = idx.diasTrabajados >= 0 ? leerNumero(fila[idx.diasTrabajados]) : undefined;
    const imp = idx.importe >= 0 ? leerNumero(fila[idx.importe]) : undefined;

    const diasCalendario = Number(e.diasCalendario) || 0;
    if (incluir === null) renglon.errores.push('Incluir debe ser "Sí" o "No"');
    if (Number.isNaN(inc)) renglon.errores.push("Incidencias no es un número");
    else if (inc !== undefined && (inc < 0 || inc > diasCalendario)) {
      renglon.errores.push(`Incidencias debe estar entre 0 y ${diasCalendario} (días del ejercicio)`);
    }
    if (Number.isNaN(trab)) renglon.errores.push("Días trabajados no es un número");
    else if (trab !== undefined && (trab < 0 || trab > DIAS_MAXIMOS)) {
      renglon.errores.push(`Días trabajados debe estar entre 0 y ${DIAS_MAXIMOS}`);
    }
    if (Number.isNaN(imp)) renglon.errores.push("Importe no es un número");
    else if (imp !== undefined && imp < 0) renglon.errores.push("El importe no puede ser negativo");
    if (renglon.errores.length) {
      renglon.estado = "error";
      continue;
    }

    const antes = {
      incluido: !!e.incluido && !e.noAplicable,
      diasIncidencias: Number(e.diasIncidencias) || 0,
      diasTrabajados: Number(e.diasTrabajados) || 0,
      importe: redondear2(e.importe),
      importeManual: !!e.importeManual,
    };

    let incluido = antes.incluido;
    if (incluir !== undefined && incluir !== antes.incluido) {
      if (incluir && e.noAplicable) renglon.avisos.push(`No se puede incluir: ${e.noAplicable}`);
      else incluido = incluir;
    }
    if (incluido && e.yaTieneAguinaldo && !antes.incluido) {
      renglon.avisos.push("Ya tiene aguinaldo de este ejercicio: se le pagaría otra vez");
    }

    let diasIncidencias = antes.diasIncidencias;
    let incidenciasManual = !!e.incidenciasManual;
    const incCambio = inc !== undefined && inc !== antes.diasIncidencias;
    if (incCambio) {
      diasIncidencias = inc;
      incidenciasManual = true;
    }

    let diasTrabajados = antes.diasTrabajados;
    if (trab !== undefined && trab !== antes.diasTrabajados) diasTrabajados = trab;
    else if (incCambio) diasTrabajados = Math.max(0, diasCalendario - inc);

    const formula = calcularImporte(e.diasAguinaldo, diasAnio, diasTrabajados, e.salarioDiario);
    let importe = antes.importe;
    let importeManual = antes.importeManual;
    if (imp !== undefined && Math.abs(imp - antes.importe) >= 0.005) {
      if (Math.abs(imp - formula) <= TOLERANCIA_IMPORTE) {
        importe = formula;
        importeManual = false;
      } else {
        importe = redondear2(imp);
        importeManual = true;
      }
    } else if (diasTrabajados !== antes.diasTrabajados) {
      importe = formula;
      importeManual = false;
    }

    if (incluido !== antes.incluido) renglon.cambios.push({ campo: "Incluir", antes: siNo(antes.incluido), despues: siNo(incluido) });
    if (diasIncidencias !== antes.diasIncidencias) {
      renglon.cambios.push({ campo: "Incidencias", antes: fmtNum(antes.diasIncidencias), despues: fmtNum(diasIncidencias) });
    }
    if (diasTrabajados !== antes.diasTrabajados) {
      renglon.cambios.push({ campo: "Días trabajados", antes: fmtNum(antes.diasTrabajados), despues: fmtNum(diasTrabajados) });
    }
    if (importe !== antes.importe || importeManual !== antes.importeManual) {
      renglon.cambios.push({
        campo: "Importe",
        antes: `${fmtMXN(antes.importe)}${antes.importeManual ? " (manual)" : ""}`,
        despues: `${fmtMXN(importe)}${importeManual ? " (manual)" : ""}`,
      });
    }

    if (renglon.cambios.length) {
      renglon.estado = "cambia";
      renglon.nuevo = {
        ...e, incluido, diasIncidencias, incidenciasManual, diasTrabajados, importe, importeManual, editado: true,
      };
    }
  }

  const contar = (estado) => renglones.filter((r) => r.estado === estado).length;
  return {
    renglones,
    avisos,
    resumen: {
      conCambios: contar("cambia"),
      sinCambios: contar("igual"),
      conError: contar("error"),
      // Empleados del borrador que no vienen en el archivo: se quedan como están.
      noVienen: empleados.filter((e) => !vistos.has(String(e.empleadoId))).length,
    },
  };
}

/** Las filas del borrador con los cambios del análisis aplicados (sólo los renglones "cambia"). */
export function aplicarCambiosEdicion(empleados = [], analisis) {
  const nuevos = new Map(
    (analisis?.renglones || []).filter((r) => r.estado === "cambia" && r.nuevo).map((r) => [r.empleadoId, r.nuevo])
  );
  return empleados.map((e) => nuevos.get(String(e.empleadoId)) || e);
}

export { COLUMNAS };
